import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';
import Alert from '../shared/TailwindComponents/Alert';
import Button from '../shared/TailwindComponents/Button';
import { Account, getAccount, resendVerificationEmail } from '../../services/api/accountService';
import { errorMessage } from '../../services/api/marketplaceService';

/**
 * Writes need a confirmed email (the backend answers EMAIL_NOT_VERIFIED).
 * Auth0 sent the link at sign-up; once it is clicked, a fresh token carries
 * the claim, so "I've confirmed" refreshes the token and asks again.
 * "Send the email again" goes through the backend, which throttles it.
 */
const EmailConfirmBanner: React.FC<{ onConfirmed: (a: Account) => void }> = ({ onConfirmed }) => {
  const { t } = useTranslation();
  const { getAccessTokenSilently, user } = useAuth0();
  const [checking, setChecking] = useState(false);
  const [stillPending, setStillPending] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendNote, setResendNote] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(0);

  // Tick the resend cooldown down once a second while it runs.
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const recheck = async () => {
    setChecking(true);
    try {
      await getAccessTokenSilently({ cacheMode: 'off' });
      const account = await getAccount();
      setStillPending(!account.email_verified);
      onConfirmed(account);
    } catch {
      setStillPending(true);
    } finally {
      setChecking(false);
    }
  };

  const resend = async () => {
    setResending(true);
    setResendNote(null);
    try {
      const result = await resendVerificationEmail();
      if (result.status === 'already_verified') {
        await recheck();
        return;
      }
      setResendNote(t('Sent. Check your inbox.'));
      setCooldown(result.retry_after ?? 60);
    } catch (err) {
      setResendNote(errorMessage(err, t("We couldn't send the email right now. Try again in a few minutes.")));
      const retryAfter = (err as { response?: { data?: { details?: { retry_after?: number } } } })
        ?.response?.data?.details?.retry_after;
      if (retryAfter) setCooldown(Math.min(retryAfter, 60 * 60));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="space-y-2">
      <Alert
        type="warning"
        message={t('Confirm your email to send requests')}
        subMessage={t('We sent a link to {{email}}. Click it, then come back here. Check your spam folder if you cannot find it.', {
          email: user?.email || t('your email'),
        })}
      />
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="secondary" size="small" onClick={recheck} isLoading={checking}>
          {t("I've confirmed my email")}
        </Button>
        <Button variant="tertiary" size="small" onClick={resend} isLoading={resending} disabled={cooldown > 0}>
          {cooldown > 120 ? t('Try again later')
            : cooldown > 0 ? t('Send again in {{seconds}} s', { seconds: cooldown })
            : t('Send the email again')}
        </Button>
        {stillPending && !checking && (
          <span className="text-sm text-fg-muted">{t('Not confirmed yet. Click the link in the email first.')}</span>
        )}
        {resendNote && !stillPending && <span className="text-sm text-fg-muted">{resendNote}</span>}
      </div>
    </div>
  );
};

export default EmailConfirmBanner;
